"use client"
import { useTranslations } from "next-intl"

import { CircleCheck, Info, OctagonX, TriangleAlert, X, type LucideIcon } from "lucide-react"
import { Toaster as SonnerToaster, toast as sonnerToast } from "sonner"

import { translate } from "@/lib/i18n/imperative"
import { Button } from "@/components/ui/button"

/**
 * EVERY toast in the app: what one looks like, how long it lives, and where they stack.
 *
 * There were two systems and they disagreed about all three.
 *
 *   · This file's `toast({ text, icon })`, used by bookmarks, feedback and copy-to-clipboard —
 *     a custom card with an icon in a disc.
 *   · Raw `sonner` — `toast.error("…", { description })` and
 *     `toast("Title", { description, duration: Infinity, action: { label: "Dismiss" } })` —
 *     used by sign-in, sign-up, preferences, the research canvas, the job poller, projects and
 *     the read page. Sonner's own default rendering, nothing like the first.
 *
 * So the same product told you a bookmark was saved in one visual language and that your session
 * had expired in another, and whether a message stayed on screen depended on which file it was
 * raised from. Worse, three pages each mounted their OWN `<Toaster>` on top of the global one:
 * sonner renders every toast once per mounted Toaster, so a sign-in error appeared twice.
 *
 * There is one Toaster now — `AppToaster`, mounted once in providers — and one way to raise a
 * message. Both are defined here, because the container's position and the card's shape are the
 * same design decision and were previously made in different files.
 *
 * The API deliberately mirrors sonner's, so `toast.error("…", { description })` reads the same
 * as before: for most call sites the migration was changing the import and nothing else.
 */

export type ToastTone = "success" | "error" | "warning" | "info"

/** The icon each tone speaks with, so no call site has to choose one. */
const TONE_ICON: Record<ToastTone, LucideIcon> = {
  success: CircleCheck,
  error: OctagonX,
  warning: TriangleAlert,
  info: Info,
}

/**
 * How long each tone stays.
 *
 * Failures last longer than confirmations: a confirmation tells you what you already know you
 * asked for, while a failure is news, and often has a reason attached that takes a moment to
 * read. `dismiss: true` overrides both and waits for the reader.
 */
const TONE_DURATION: Record<ToastTone, number> = {
  success: 3000,
  info: 3000,
  warning: 5000,
  error: 5000,
}

export interface ToastOptions {
  /*
    KEYS, not words — resolved in `show` below against the reader's language.

    A toast fires from anywhere: an event handler, a promise, a library module in `lib/` with no
    component around it. None of those can hold a `useTranslations` hook, so the copy cannot be
    translated where it is written; it is translated where it is DRAWN, which is here. See
    lib/i18n/imperative for how a non-component reaches the messages at all.
  */
  /*
    A toast's words come from one of two places, and the difference matters.

    `text` is a message that ARRIVED already worded — a server's reason for refusing, an error
    string from an API. It is passed through untouched, because inventing a translation for it
    would be inventing the text.

    `textKey` is a message of OURS, and it is translated. Where both are given, the server's
    words win and the key is the fallback for when it did not send any.
  */
  /** Key for the message. One line, sentence case. */
  textKey?: string
  /** A message that arrived already worded. Wins over `textKey`. */
  text?: string
  /** Values for a `textKey` that interpolates — see `preferences.updateFailed`. */
  textValues?: Record<string, string | number>
  /** Key for the detail under it. */
  descriptionKey?: string
  /** Literal detail — a server's reason for refusing, which arrives already worded. */
  description?: string
  tone?: ToastTone
  /** Overrides the tone's icon. Rarely needed; `Check` for copy-to-clipboard is the exception. */
  icon?: LucideIcon
  /** Stay until dismissed, with an X to do it. For anything the reader must actually read. */
  dismiss?: boolean
  /** Overrides the tone's duration. */
  duration?: number
}

/*
  Where the `toast.` in `toast.auth.signedOut.text` comes from.

  `MESSAGES` writes its keys relative to the catalogue's `toast` namespace — `auth.signedOut.text`
  — because that is the namespace every one of them lives in and repeating it 54 times says
  nothing. `translate()` is not namespaced: it is the app's general way out of React and takes a
  full path. Something has to join the two, and it is this, once, rather than every entry.

  Nothing did, originally. Every key resolved against the TOP-LEVEL namespace of its first
  segment — `auth`, `read`, `search`, `feedback`, all of which exist and none of which have
  these keys — so all 54 messages rendered their own key path as the toast's text, in all three
  languages. It was invisible because a missing message returns the key rather than throwing,
  which is the right behaviour for a toast and is exactly what hid this.
*/
const NAMESPACE = "toast."

function show(options: ToastOptions) {
  const tone = options.tone ?? "info"
  const text =
    options.text ??
    (options.textKey ? translate(NAMESPACE + options.textKey, options.textValues) : "")
  /*
    A server's reason is passed through untranslated, deliberately. It arrives already worded,
    from an API that knows nothing about the reader's language, and inventing a translation for
    it would be inventing the text. Where the detail is OURS it has a key instead.
  */
  const description = options.descriptionKey
    ? translate(NAMESPACE + options.descriptionKey)
    : options.description
  return sonnerToast.custom(
    (id) => (
      <ToastCard
        id={id}
        text={text}
        description={description}
        icon={options.icon ?? TONE_ICON[tone]}
        dismiss={options.dismiss}
      />
    ),
    { duration: options.dismiss ? Infinity : (options.duration ?? TONE_DURATION[tone]) },
  )
}

/**
 * Raise a toast.
 *
 * Takes either an options object — `toast({ text, icon })`, the form this file always had — or
 * a message and options, which is sonner's form and what the migrated call sites use.
 */
function toastFn(input: ToastOptions | string, options?: Omit<ToastOptions, "text">) {
  return show(typeof input === "string" ? { ...options, text: input } : input)
}

/** `toast.error("…", { description })`, matching sonner's shape so call sites read unchanged. */
const withTone =
  (tone: ToastTone) =>
  (text: string, options?: Omit<ToastOptions, "text" | "tone">) =>
    show({ ...options, text, tone })

export const toast = Object.assign(toastFn, {
  success: withTone("success"),
  error: withTone("error"),
  warning: withTone("warning"),
  info: withTone("info"),
  dismiss: (id?: string | number) => sonnerToast.dismiss(id),
})

function ToastCard({
  id,
  text,
  description,
  icon: Icon,
  dismiss,
}: {
  id: string | number
  text: string
  description?: string
  icon?: LucideIcon
  dismiss?: boolean
}) {
  const t = useTranslations("ui.toast")
  return (
    /*
      `rounded-2xl` and a border, like every other card in the app. It was `rounded-md` with a
      shadow and no border, which is the one surface here that looked like stock shadcn.

      A plain string rather than `cn`: lib/utils imports `toast` from this file for
      copy-to-clipboard, so importing `cn` back out of it made a genuine circular import. There
      is nothing conditional here to merge anyway.
    */
    <div className="flex w-full flex-row items-start gap-4 rounded-2xl bg-background p-4 shadow-[0_0_24px_rgba(0,0,0,0.08)] md:w-[364px]">
      {Icon && (
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-secondary">
          <Icon className="size-4 shrink-0 text-foreground" strokeWidth={2} />
        </span>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-1 py-1.5">
        <span className="text-sm font-medium leading-snug text-foreground">{text}</span>
        {description && (
          <span className="text-[13px] font-normal leading-relaxed text-muted-foreground text-pretty">
            {description}
          </span>
        )}
      </div>

      {dismiss && (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t("dismiss")}
          onClick={() => sonnerToast.dismiss(id)}
          className="shrink-0 text-muted-foreground hover:text-foreground"
        >
          <X />
        </Button>
      )}
    </div>
  )
}

/**
 * The one Toaster. Mounted once, in providers.
 *
 * Never mount a second one anywhere: sonner renders each toast once per Toaster on the page, so
 * a page with its own becomes a page that shows everything twice. Three pages had one.
 */
export function AppToaster() {
  return <SonnerToaster position="bottom-right" gap={12} offset={24} />
}
